import { useCallback, useRef, useState, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AnimatePresence, animate, motion, useMotionValue, useTransform, type AnimationPlaybackControls } from 'framer-motion'
import { cn } from '@/lib/utils'

/**
 * Mobile bottom sheet: slides up from the bottom edge, dismissed by tapping the scrim or by
 * dragging it down from anywhere, the way iOS sheets work:
 *  - the sheet follows the finger and the scrim fades with it; a flick or a drag past ~40%
 *    of its height dismisses it, anything less springs back;
 *  - content scrolled down scrolls first; once it's at the top, a downward drag moves the
 *    sheet (as a new gesture, like iOS);
 *  - horizontal swipes and touches starting in a text field are left alone (add
 *    data-sheet-no-drag to opt out anything else);
 *  - starting a drag dismisses the keyboard, and a drag never also counts as a tap.
 * A mouse can drag the grabber/title row.
 */
interface BottomSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Visually hide the title row (still announced to screen readers). */
  hideTitle?: boolean
  /** Rendered right-aligned in the title row, e.g. an action button. */
  titleAccessory?: ReactNode
  children: ReactNode
  className?: string
}

// Movement before a touch counts as a drag or a scroll (about iOS's touch slop).
const SLOP_PX = 8
// Downward speed (px/ms) that dismisses regardless of distance: a flick.
const FLICK_SPEED = 0.45
// How far (share of the sheet's height) a release, plus a little momentum, must reach to dismiss.
const DISMISS_SHARE = 0.4
const NO_DRAG = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-sheet-no-drag]'
const SPRING = { type: 'spring', damping: 34, stiffness: 420 } as const

interface Gesture {
  startX: number
  startY: number
  /** Where the sheet drag began (after the slop), so it doesn't jump by the slop distance. */
  originY: number
  /** The scrollable element the touch started in, if any. */
  scroller: HTMLElement | null
  mode: 'pending' | 'drag' | 'scroll' | 'ignore'
  samples: { y: number; t: number }[]
}

function scrollableAncestor(target: Element, sheet: Element): HTMLElement | null {
  for (let el: Element | null = target; el && el !== sheet; el = el.parentElement) {
    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight + 1
      && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) return el
  }
  return null
}

/** iOS-style rubber band: resistance grows the further past the edge you pull. */
function rubberBand(distance: number, dimension: number): number {
  return (1 - 1 / ((distance * 0.55) / dimension + 1)) * dimension
}

function useSheetDrag(onDismiss: () => void) {
  const dragY = useMotionValue(0)
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const gesture = useRef<Gesture | null>(null)
  const didDrag = useRef(false)
  const settling = useRef<AnimationPlaybackControls | null>(null)
  // Set when a drag has already carried the sheet off-screen, so closing skips the slide-out.
  const [flungAway, setFlungAway] = useState(false)
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  const height = () => sheetRef.current?.offsetHeight || window.innerHeight

  /** Returns true if the gesture is now dragging the sheet (the caller then owns the event). */
  const move = useCallback((x: number, y: number): boolean => {
    const g = gesture.current
    if (!g || g.mode === 'scroll' || g.mode === 'ignore') return false
    const dx = x - g.startX
    const dy = y - g.startY

    if (g.mode === 'pending') {
      if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return false
      if (Math.abs(dx) > Math.abs(dy)) { g.mode = 'ignore'; return false }
      // Down: drag the sheet unless the content is scrolled. Up: scroll content if there is any.
      const canDrag = dy > 0 ? !g.scroller || g.scroller.scrollTop <= 0 : !g.scroller
      if (!canDrag) { g.mode = 'scroll'; return false }
      g.mode = 'drag'
      g.originY = y
      settling.current?.stop()
      const active = document.activeElement
      if (active instanceof HTMLElement && sheetRef.current?.contains(active)) active.blur()
    }

    const offset = y - g.originY
    dragY.set(offset >= 0 ? offset : -rubberBand(-offset, height()))
    const now = performance.now()
    g.samples.push({ y, t: now })
    while (g.samples.length > 2 && now - g.samples[0]!.t > 100) g.samples.shift()
    return true
  }, [dragY])

  const end = useCallback((cancelled = false) => {
    const g = gesture.current
    gesture.current = null
    if (!g || g.mode !== 'drag') return
    didDrag.current = true

    const first = g.samples[0]
    const last = g.samples[g.samples.length - 1]
    const velocity = first && last && last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0 // px/ms
    const current = dragY.get()
    const h = height()
    const dismiss = !cancelled && current > 0
      && (velocity > FLICK_SPEED || current + velocity * 150 > h * DISMISS_SHARE)

    if (dismiss) {
      // Carry the finger's momentum off-screen, then close (the scrim fades along with it).
      const speed = Math.max(velocity, 1.2) // px/ms
      const duration = Math.min(0.28, Math.max(0.12, (h - current) / speed / 1000))
      // Flag it first: AnimatePresence exits with the props from the render before closing.
      setFlungAway(true)
      settling.current = animate(dragY, h, { duration, ease: 'easeOut' })
      void settling.current.then(() => onDismissRef.current())
    } else {
      settling.current = animate(dragY, 0, { ...SPRING, velocity: velocity * 1000 })
    }
  }, [dragY])

  const start = useCallback((x: number, y: number, target: Element) => {
    const sheet = sheetRef.current
    didDrag.current = false
    if (!sheet || target.closest(NO_DRAG)) { gesture.current = null; return }
    gesture.current = {
      startX: x, startY: y, originY: y, mode: 'pending',
      scroller: scrollableAncestor(target, sheet),
      samples: [{ y, t: performance.now() }],
    }
  }, [])

  // Touch listeners go on directly (not via React props, which are passive) so a sheet drag
  // can preventDefault the touchmove and stop the page or content from scrolling underneath.
  const ref = useCallback((sheet: HTMLDivElement | null) => {
    sheetRef.current = sheet
    if (!sheet) return
    dragY.jump(0)
    setFlungAway(false)

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { end(true); return }
      const t = e.touches[0]!
      start(t.clientX, t.clientY, e.target as Element)
    }
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) { end(true); return }
      const t = e.touches[0]!
      if (move(t.clientX, t.clientY) && e.cancelable) e.preventDefault()
    }
    const onTouchEnd = () => end()
    const onTouchCancel = () => end(true)

    sheet.addEventListener('touchstart', onTouchStart, { passive: true })
    sheet.addEventListener('touchmove', onTouchMove, { passive: false })
    sheet.addEventListener('touchend', onTouchEnd)
    sheet.addEventListener('touchcancel', onTouchCancel)
    return () => {
      sheet.removeEventListener('touchstart', onTouchStart)
      sheet.removeEventListener('touchmove', onTouchMove)
      sheet.removeEventListener('touchend', onTouchEnd)
      sheet.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [dragY, start, move, end])

  /** Mouse drags from the grabber/title row (touch is handled on the whole sheet). */
  const onHandlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return
    start(e.clientX, e.clientY, e.target as Element)
    const onMove = (ev: PointerEvent) => { move(ev.clientX, ev.clientY) }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      end()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [start, move, end])

  /** Swallows the click that ends a drag, so dragging from a button doesn't press it. */
  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (!didDrag.current) return
    didDrag.current = false
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const scrimOpacity = useTransform(dragY, (v) => (v <= 0 ? 1 : Math.max(0, 1 - v / height())))

  return { ref, dragY, scrimOpacity, flungAway, onHandlePointerDown, onClickCapture }
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  titleAccessory,
  children,
  className,
}: BottomSheetProps) {
  const drag = useSheetDrag(() => onOpenChange(false))

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: drag.flungAway ? 0 : 0.2 } }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-50"
              >
                <motion.div className="absolute inset-0 bg-black/50" style={{ opacity: drag.scrimOpacity }} />
              </motion.div>
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              forceMount
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {/* Outer layer slides in and out; the inner sheet follows the finger. */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%', transition: drag.flungAway ? { duration: 0 } : SPRING }}
                transition={SPRING}
                className="fixed inset-x-0 bottom-0 z-50"
              >
                <motion.div
                  ref={drag.ref}
                  style={{ y: drag.dragY }}
                  onClickCapture={drag.onClickCapture}
                  className={cn(
                    'flex flex-col rounded-t-2xl border-t border-border bg-background shadow-[0_-8px_30px_rgba(0,0,0,0.18)]',
                    'max-h-[85dvh] pb-[var(--sab)]',
                    className,
                  )}
                >
                  {/* Grabber + title */}
                  <div
                    className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
                    onPointerDown={drag.onHandlePointerDown}
                  >
                    <div className="flex justify-center pt-3 pb-2" aria-hidden>
                      <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
                    </div>
                    <div
                      className={cn(
                        'flex items-center justify-between gap-2 px-5 pb-3',
                        hideTitle && 'sr-only',
                      )}
                    >
                      <DialogPrimitive.Title className="text-base font-semibold text-foreground">
                        {title}
                      </DialogPrimitive.Title>
                      {titleAccessory}
                    </div>
                  </div>

                  {/* Scrollable body */}
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    {children}
                  </div>
                </motion.div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  )
}
